<template>
  <div class="layout">
    <Sidebar @toggle-sidebar="toggleSidebar" />
    <div class="main-content" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
      <div class="homepage-container">
        <!-- Toast Notification -->
        <div v-if="toast.show" class="toast-notification" :class="toast.type">
          {{ toast.message }}
        </div>
        
        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay">
          <div class="modal-container">
            <div class="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to delete this section? This action cannot be undone.</p>
            </div>
            <div class="modal-footer">
              <button class="cancel-button" @click="cancelDelete">Cancel</button>
              <button class="delete-button" @click="deleteSection" :disabled="isUpdating">
                <span v-if="isUpdating">Deleting...</span>
                <span v-else>Delete</span>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Section Details Modal -->
        <div v-if="showDetailsModal" class="modal-overlay">
          <div class="modal-container details-modal">
            <div class="modal-header">
              <h3>Section Details</h3>
              <button class="close-button" @click="closeDetailsModal">&times;</button>
            </div>
            <div class="modal-body">
              <div v-if="isDetailLoading" class="detail-loading">
                <div class="loader-spinner"></div>
                <span>Loading section details...</span>
              </div>
              <div v-else-if="detailError" class="detail-error">
                {{ detailError }}
              </div>
              <div v-else-if="selectedSectionDetails" class="section-details">
                <div class="detail-row">
                  <div class="detail-label">ID:</div>
                  <div class="detail-value">{{ selectedSectionDetails.id }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Title:</div>
                  <div class="detail-value">{{ selectedSectionDetails.title }}</div>
                </div>
                <div class="detail-row" v-if="selectedSectionDetails.title_ar">
                  <div class="detail-label">Arabic Title:</div>
                  <div class="detail-value">{{ selectedSectionDetails.title_ar }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Type:</div>
                  <div class="detail-value">{{ selectedSectionDetails.type }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Order:</div>
                  <div class="detail-value">{{ selectedSectionDetails.order }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Active:</div>
                  <div class="detail-value">{{ selectedSectionDetails.is_active ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Dynamic:</div>
                  <div class="detail-value">{{ selectedSectionDetails.is_dynamic ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Cache Key:</div>
                  <div class="detail-value">{{ selectedSectionDetails.cache_key }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">TTL:</div>
                  <div class="detail-value">{{ selectedSectionDetails.ttl }} seconds</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Method Name:</div>
                  <div class="detail-value">{{ selectedSectionDetails.method_name }}</div>
                </div>
                <div class="detail-row" v-if="selectedSectionDetails.additional_config">
                  <div class="detail-label">Additional Config:</div>
                  <div class="detail-value">
                    <pre>{{ JSON.stringify(selectedSectionDetails.additional_config, null, 2) }}</pre>
                  </div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Created At:</div>
                  <div class="detail-value">{{ formatDate(selectedSectionDetails.created_at) }}</div>
                </div>
                <div class="detail-row">
                  <div class="detail-label">Updated At:</div>
                  <div class="detail-value">{{ formatDate(selectedSectionDetails.updated_at) }}</div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="cancel-button" @click="closeDetailsModal">Close</button>
              <button class="edit-button" @click="editSection(selectedSectionDetails?.id)" v-if="selectedSectionDetails">Edit</button>
            </div>
          </div>
        </div>
        
        <div class="page-header">
          <h1>Homepage Sections</h1>
          <div class="header-actions">
            <button class="preview-toggle" @click="togglePreviewMode">
              <i :class="['fas', isPreviewMode ? 'fa-edit' : 'fa-eye']"></i>
              {{ isPreviewMode ? 'Edit Mode' : 'Preview Mode' }}
            </button>
            <div class="search-filter">
              <input type="text" v-model="searchTerm" placeholder="Search sections..." class="search-input" />
              <select v-model="statusFilter" class="filter-dropdown">
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
              </select>
              <select v-model="typeFilter" class="filter-dropdown">
                <option value="all">All Types</option>
                <option value="Banner">Banner</option>
                <option value="FullCardCarousel">Full Card Carousel</option>
                <!-- ... other section types ... -->
              </select>
            </div>
            <button class="create-button" @click="navigateToCreate">
              Create New Section
            </button>
          </div>
        </div>
        
        <div v-if="isLoading" class="loading-state">
          <div v-for="n in 3" :key="n" class="loading-section">
            <div class="loading-shimmer"></div>
          </div>
        </div>

        <div v-else-if="error" class="error">{{ error }}</div>

        <template v-else>
          <draggable 
            v-model="orderedSections" 
            item-key="id"
            handle=".drag-handle"
            @end="updateSectionOrder"
            class="sections-container"
            :disabled="isUpdating">
            <template #item="{element: section}">
              <div class="section-wrapper" :class="{ 'updating': isUpdating }" @click="(event) => viewSectionDetails(section.id, event)">
                <!-- Loading overlay for updating sections -->
                <div v-if="isUpdating" class="section-loader">
                  <div class="loader-spinner"></div>
                </div>
                <div class="drag-handle">
                  <i class="fas fa-grip-lines"></i>
                </div>
                <div class="section-status">
                  <span :class="['status-badge', section.status]">{{ section.status }}</span>
                  <span v-if="section.scheduledDate" class="scheduled-date">
                    Scheduled: {{ formatDate(section.scheduledDate) }}
                  </span>
                </div>
                <div class="section-actions">
                  <button class="action-icon edit" @click="editSection(section.id)" title="Edit Section">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="action-icon duplicate" @click="duplicateSection(section.id)" title="Duplicate">
                    <i class="fas fa-copy"></i>
                  </button>
                  <button class="action-icon delete" @click="confirmDelete(section.id)" title="Delete">
                    <i class="fas fa-trash"></i>
                  </button>
                  <button class="action-icon visibility" @click="toggleVisibility(section.id)" :title="section.active ? 'Hide Section' : 'Show Section'">
                    <i :class="section.active ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                  </button>
                </div>
                <!-- Banner Section -->
                <div v-if="section.type === 'Banner'" class="banner-section">
                  <h2>{{ section.title }}</h2>
                  <!-- Banner content will be added here -->
                </div>

                <!-- Full Card Carousel Section -->
                <div v-else-if="section.type === 'FullCardCarousel'" class="full-card-section">
                  <div class="section-header">
                    <h2>{{ section.title }}</h2>
                    <p v-if="section.subtitle" class="subtitle">{{ section.subtitle }}</p>
                  </div>
                  <div class="carousel-wrapper">
                    <!-- Additional cards from config -->
                    <div v-if="section.additional_config?.additionalCards" class="additional-cards">
                      <div v-for="(card, index) in section.additional_config.additionalCards" 
                           :key="index" 
                           class="additional-card">
                        <h3>{{ card.title }}</h3>
                        <p>{{ card.subtitle }}</p>
                        <button v-if="card.action" 
                                class="action-button"
                                @click="handleAction(card.action)">
                          {{ card.action.text }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Square Card Carousel V1 Section -->
                <div v-else-if="section.type === 'SquareCardCarouselV1'" class="square-card-section">
                  <div class="section-header">
                    <h2>{{ section.title }}</h2>
                  </div>
                  <div class="carousel-wrapper">
                    <!-- Square cards content will be added here -->
                  </div>
                </div>

                <!-- Tall Card Carousel Section -->
                <div v-else-if="section.type === 'TallCardCarousel'" class="tall-card-section">
                  <div class="section-header">
                    <h2>{{ section.title }}</h2>
                  </div>
                  <div class="carousel-wrapper">
                    <!-- Tall cards content will be added here -->
                  </div>
                </div>

                <!-- Grid Carousel Section -->
                <div v-else-if="section.type === 'GridCarousel'" class="grid-section">
                  <div class="section-header">
                    <h2>{{ section.title }}</h2>
                  </div>
                  <div class="grid-wrapper">
                    <!-- Grid content will be added here -->
                  </div>
                </div>

                <!-- Square Card Carousel V2 Section -->
                <div v-else-if="section.type === 'SquareCardCarouselV2'" class="square-card-v2-section">
                  <div class="section-header">
                    <h2>{{ section.title }}</h2>
                  </div>
                  <div class="carousel-wrapper">
                    <!-- Square V2 cards content will be added here -->
                  </div>
                </div>

                <div class="analytics-toggle" @click="toggleAnalytics(section.id)">
                  <span>Performance Metrics</span>
                  <i :class="['fas', section.showAnalytics ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                </div>
                <div v-if="section.showAnalytics" class="analytics-panel">
                  <div class="metrics-grid">
                    <div class="metric">
                      <span class="metric-value">{{ section.metrics?.views || 0 }}</span>
                      <span class="metric-label">Views</span>
                    </div>
                    <div class="metric">
                      <span class="metric-value">{{ section.metrics?.clicks || 0 }}</span>
                      <span class="metric-label">Clicks</span>
                    </div>
                    <div class="metric">
                      <span class="metric-value">{{ calculateCTR(section) }}%</span>
                      <span class="metric-label">CTR</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import draggable from 'vuedraggable';

export default {
  name: 'HomePage',
  components: {
    Sidebar,
    draggable
  },
  setup() {
    const router = useRouter();
    const sections = ref([]);
    const isLoading = ref(true);
    const isSidebarCollapsed = ref(false);
    const error = ref(null);
    const searchTerm = ref('');
    const statusFilter = ref('all');
    const typeFilter = ref('all');
    const isPreviewMode = ref(false);
    const showDeleteModal = ref(false);
    const sectionToDelete = ref(null);
    const isUpdating = ref(false);
    const showDetailsModal = ref(false);
    const selectedSectionDetails = ref(null);
    const isDetailLoading = ref(false);
    const detailError = ref(null);

    // Add necessary refs for analytics and functionality
    const toast = reactive({
      show: false,
      message: '',
      type: 'success'
    });

    const sortedSections = computed(() => {
      return [...sections.value].sort((a, b) => a.order - b.order);
    });

    const orderedSections = computed(() => {
      // Apply filters to the sections
      let filteredSections = [...sections.value];
      
      // Apply search filter
      if (searchTerm.value) {
        const term = searchTerm.value.toLowerCase();
        filteredSections = filteredSections.filter(section => 
          section.title.toLowerCase().includes(term) || 
          (section.subtitle && section.subtitle.toLowerCase().includes(term))
        );
      }
      
      // Apply status filter
      if (statusFilter.value !== 'all') {
        filteredSections = filteredSections.filter(section => 
          section.status === statusFilter.value
        );
      }
      
      // Apply type filter
      if (typeFilter.value !== 'all') {
        filteredSections = filteredSections.filter(section => 
          section.type === typeFilter.value
        );
      }
      
      // Sort by order
      return filteredSections.sort((a, b) => a.order - b.order);
    });

    const fetchSections = async () => {
      isLoading.value = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/admin/homepage-sections', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Add showAnalytics property to each section
        sections.value = response.data.data.map(section => ({
          ...section,
          showAnalytics: false,
          metrics: section.metrics || { views: 0, clicks: 0 }
        }));
      } catch (err) {
        error.value = 'Failed to load homepage sections';
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    const handleAction = (action) => {
      if (action.link) {
        if (action.link.startsWith('http')) {
          // External link
          window.open(action.link, '_blank');
        } else {
          // Internal route
          router.push(action.link);
        }
      } else if (action.callback && typeof action.callback === 'function') {
        // Execute callback function if provided
        action.callback();
      }
    };

    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
    };

    const navigateToCreate = () => {
      router.push('/homepage/create');
    };

    const editSection = (id) => {
      router.push(`/homepage/edit/${id}`);
    };

    const duplicateSection = async (id) => {
      try {
        isUpdating.value = true;
        const token = localStorage.getItem('token');
        const sectionToDuplicate = sections.value.find(s => s.id === id);
        
        if (!sectionToDuplicate) return;
        
        // Create a deep copy of the section without the id
        const sectionData = { ...sectionToDuplicate };
        delete sectionData.id;
        
        // Set a new title to indicate it's a copy
        sectionData.title = `${sectionData.title} (Copy)`;
        sectionData.order = Math.max(...sections.value.map(s => s.order), 0) + 1;
        sectionData.status = 'draft';
        
        // Ensure additional_config is properly stringified if it exists
        if (sectionData.additional_config !== null && typeof sectionData.additional_config === 'object') {
          sectionData.additional_config = JSON.stringify(sectionData.additional_config);
        }
        
        const response = await axios.post('https://api.kewaar.com/admin/homepage-sections', sectionData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Add the new section to the list
        sections.value.push({
          ...response.data.data,
          showAnalytics: false
        });
        
        showToast('Section duplicated successfully', 'success');
      } catch (err) {
        console.error('Failed to duplicate section:', err);
        showToast(`Failed to duplicate section: ${err.response?.data?.message || err.message}`, 'error');
      } finally {
        isUpdating.value = false;
      }
    };

    const showToast = (message, type = 'success') => {
      toast.show = true;
      toast.message = message;
      toast.type = type;
      
      // Hide toast after 3 seconds
      setTimeout(() => {
        toast.show = false;
      }, 3000);
    };

    const confirmDelete = (id) => {
      sectionToDelete.value = id;
      showDeleteModal.value = true;
    };
    
    const cancelDelete = () => {
      showDeleteModal.value = false;
      sectionToDelete.value = null;
    };
    
    const deleteSection = async () => {
      if (!sectionToDelete.value) return;
      
      try {
        isUpdating.value = true;
        const token = localStorage.getItem('token');
        await axios.delete(`https://api.kewaar.com/admin/homepage-sections/${sectionToDelete.value}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Remove the section from the list
        sections.value = sections.value.filter(s => s.id !== sectionToDelete.value);
        showToast('Section deleted successfully', 'success');
      } catch (err) {
        console.error('Failed to delete section:', err);
        showToast('Failed to delete section', 'error');
      } finally {
        showDeleteModal.value = false;
        sectionToDelete.value = null;
        isUpdating.value = false;
      }
    };

    const toggleVisibility = async (id) => {
      try {
        isUpdating.value = true;
        const section = sections.value.find(s => s.id === id);
        if (!section) return;
        
        const token = localStorage.getItem('token');
        
        // Toggle the active status
        const updatedActive = !section.active;
        
        await axios.patch(`https://api.kewaar.com/admin/homepage-sections/${id}`, {
          active: updatedActive
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Update the section in the list
        section.active = updatedActive;
        showToast(`Section ${updatedActive ? 'activated' : 'deactivated'} successfully`, 'success');
      } catch (err) {
        console.error('Failed to toggle visibility:', err);
        showToast('Failed to update section visibility', 'error');
      } finally {
        isUpdating.value = false;
      }
    };

    const toggleAnalytics = (id) => {
      const section = sections.value.find(s => s.id === id);
      if (section) {
        section.showAnalytics = !section.showAnalytics;
      }
    };

    const calculateCTR = (section) => {
      if (!section.metrics) return 0;
      const { views, clicks } = section.metrics;
      
      if (!views || views === 0) return 0;
      
      // Calculate percentage with 2 decimal places
      return ((clicks / views) * 100).toFixed(2);
    };

    const updateSectionOrder = async () => {
      try {
        isUpdating.value = true;
        const token = localStorage.getItem('token');
        
        // Create an array of section IDs with their new order
        const updatedOrder = orderedSections.value.map((section, index) => ({
          id: section.id,
          order: index + 1
        }));
        
        await axios.post('https://api.kewaar.com/admin/homepage-sections/reorder', {
          sections: updatedOrder
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Update the sections with new order values
        updatedOrder.forEach(update => {
          const section = sections.value.find(s => s.id === update.id);
          if (section) {
            section.order = update.order;
          }
        });
        
        showToast('Section order updated successfully', 'success');
      } catch (err) {
        console.error('Failed to update section order:', err);
        showToast('Failed to update section order', 'error');
      } finally {
        isUpdating.value = false;
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    const togglePreviewMode = () => {
      isPreviewMode.value = !isPreviewMode.value;
    };

    const fetchSectionById = async (id) => {
      isDetailLoading.value = true;
      detailError.value = null;
      selectedSectionDetails.value = null;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`https://api.kewaar.com/api/admin/homepage-sections/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          selectedSectionDetails.value = response.data.data;
        } else {
          detailError.value = response.data.message || 'Failed to fetch section details';
        }
      } catch (err) {
        console.error('Error fetching section details:', err);
        detailError.value = 'Failed to load section details. Please try again.';
      } finally {
        isDetailLoading.value = false;
      }
    };

    const viewSectionDetails = (id, event) => {
      // Don't trigger the modal when clicking action buttons or drag handle
      const clickedElement = event.target;
      const clickedOnAction = clickedElement.closest('.section-actions') || 
                             clickedElement.closest('.drag-handle') ||
                             clickedElement.closest('.analytics-toggle');
                             
      if (clickedOnAction) return;
      
      showDetailsModal.value = true;
      fetchSectionById(id);
    };

    const closeDetailsModal = () => {
      showDetailsModal.value = false;
      selectedSectionDetails.value = null;
    };

    onMounted(() => {
      fetchSections();
    });

    return {
      sections,
      sortedSections,
      orderedSections,
      isLoading,
      isSidebarCollapsed,
      error,
      toggleSidebar,
      handleAction,
      navigateToCreate,
      editSection,
      duplicateSection,
      confirmDelete,
      cancelDelete,
      deleteSection,
      showDeleteModal,
      sectionToDelete,
      toggleVisibility,
      toggleAnalytics,
      calculateCTR,
      updateSectionOrder,
      searchTerm,
      statusFilter,
      typeFilter,
      isPreviewMode,
      togglePreviewMode,
      formatDate,
      isUpdating,
      toast,
      showDetailsModal,
      selectedSectionDetails,
      isDetailLoading,
      detailError,
      viewSectionDetails,
      closeDetailsModal
    };
  }
};
</script>

<style scoped>
.layout {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 0;
}

.homepage-container {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.search-filter {
  display: flex;
  gap: 0.5rem;
}

.search-input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.filter-dropdown {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.create-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.create-button:hover {
  background: #2563eb;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.loading-section {
  height: 200px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.section-wrapper {
  margin-bottom: 3rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.section-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 1.5rem;
}

h2 {
  color: #1e293b;
  margin: 0;
  font-size: 1.5rem;
}

.subtitle {
  color: #64748b;
  margin: 0.5rem 0 0;
  font-size: 1rem;
}

.carousel-wrapper {
  position: relative;
  overflow: hidden;
}

.banner-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.full-card-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.square-card-section,
.tall-card-section,
.grid-section,
.square-card-v2-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.additional-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.additional-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  transition: transform 0.2s ease;
}

.additional-card:hover {
  transform: translateY(-2px);
}

.additional-card h3 {
  margin: 0 0 0.5rem;
  color: #1e293b;
  font-size: 1.25rem;
}

.additional-card p {
  margin: 0 0 1rem;
  color: #64748b;
}

.action-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.action-button:hover {
  background: #2563eb;
}

.grid-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.home-section.inactive {
  opacity: 0.6;
  filter: grayscale(80%);
}

.loading, .error {
  padding: 20px;
  text-align: center;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
  
  .homepage-container {
    padding: 1rem;
  }
  
  .additional-cards {
    grid-template-columns: 1fr;
  }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  color: #1e293b;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.cancel-button {
  background-color: #e2e8f0;
  color: #1e293b;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}

.delete-button {
  background-color: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}

.delete-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Toast notification */
.toast-notification {
  position: fixed;
  top: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 6px;
  color: white;
  z-index: 1000;
  animation: fadeInOut 3s ease-in-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.toast-notification.success {
  background-color: #10b981;
}

.toast-notification.error {
  background-color: #ef4444;
}

.toast-notification.warning {
  background-color: #f59e0b;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translateY(-20px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-20px); }
}

/* Loading indicator for buttons */
.action-icon.loading {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Show loading overlay when updating */
.section-wrapper {
  position: relative;
}

.section-wrapper.updating:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.section-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 20;
  border-radius: 12px;
}

.loader-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .section-wrapper.updating:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.5);
    z-index: 10;
  }

  .section-loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 20;
  }

  .loader-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}

/* Section Details Modal Styles */
.details-modal {
  width: 600px;
  max-width: 90%;
  max-height: 80vh;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
}

.detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  gap: 1rem;
}

.detail-error {
  color: #ef4444;
  padding: 1rem;
  text-align: center;
}

.section-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 1rem;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.75rem;
}

.detail-label {
  font-weight: 600;
  color: #1e293b;
  width: 30%;
  min-width: 120px;
}

.detail-value {
  flex: 1;
  color: #64748b;
}

.detail-value pre {
  white-space: pre-wrap;
  margin: 0;
  background: #f1f5f9;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.edit-button {
  background-color: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}

.edit-button:hover {
  background-color: #2563eb;
}

/* Ensure action buttons still work without triggering the section detail view */
.section-actions button,
.drag-handle,
.analytics-toggle {
  position: relative;
  z-index: 10;
}
</style> 